import { IconX } from '@tabler/icons-react';
import React from 'react';
import { createPortal } from 'react-dom';

import { getInventoryAgeingReceipts } from '@api/inventory';
import type { InventoryAgeingReceipt, InventoryAgeingReceiptsResponse } from '@api/inventory';
import { DataSheet, dataSheetTokens } from '@components/ui/DataSheet';
import type { DataSheetColumn } from '@components/ui/DataSheet';

import { ageBucketLabels } from './inventoryAgeing';
import { formatReceiptDate } from './inventoryAgeingColumns';

interface AgeingReceiptsModalProps {
    isOpen: boolean;
    onClose: () => void;
    productId: string;
    skuLabel: string;
    bucketEdges: number[];
    /** The bucket whose cell was clicked, to call out its receipts; 'older' for the history remainder. */
    highlight: number | 'older' | null;
    /** WAREHOUSE scope: the warehouse whose row was clicked — the layers standing there, with their origin. */
    warehouseId?: string | null;
    warehouseName?: string | null;
}

/**
 * The receipts behind one SKU's ageing row. On-hand is matched to the newest receipts
 * first, so the rows at the top are what is assumed still on the shelf and the rest are
 * assumed sold — the same allocation the grid's buckets came from.
 */
const AgeingReceiptsModal: React.FC<AgeingReceiptsModalProps> = ({
    isOpen,
    onClose,
    productId,
    skuLabel,
    bucketEdges,
    highlight,
    warehouseId = null,
    warehouseName = null,
}) => {
    const [data, setData] = React.useState<InventoryAgeingReceiptsResponse | null>(null);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        if (!isOpen) return;
        setLoading(true);
        getInventoryAgeingReceipts({ productId, bucketEdges, ...(warehouseId ? { warehouseId } : {}) })
            .then(setData)
            .catch(() => setData(null))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, productId, warehouseId, bucketEdges.join(',')]);

    if (!isOpen) return null;

    const labels = ageBucketLabels(data?.bucket_edges ?? bucketEdges);
    const receipts = data?.receipts ?? [];
    const muted = { color: dataSheetTokens.colors.textMuted };
    const byWarehouse = !!warehouseId;
    const columns: DataSheetColumn<InventoryAgeingReceipt>[] = [
        {
            key: 'grn_id',
            header: byWarehouse ? 'Origin receipt' : 'Receipt',
            sticky: true,
            render: (_v, row) => (
                <span style={row.allocated ? undefined : muted}>{row.untraced ? 'untraced' : (row.grn_id ?? '—')}</span>
            ),
        },
        {
            key: 'warehouse_name',
            header: byWarehouse ? 'Received at' : 'Warehouse',
            render: (_v, row) => <span style={row.allocated ? undefined : muted}>{row.warehouse_name}</span>,
        },
        ...(byWarehouse
            ? [
                  {
                      key: 'via',
                      header: 'Arrived via',
                      render: (_v: unknown, row: InventoryAgeingReceipt) => (
                          <span style={row.allocated ? undefined : muted}>{row.via ?? 'received here'}</span>
                      ),
                  } satisfies DataSheetColumn<InventoryAgeingReceipt>,
              ]
            : []),
        { key: 'date', header: 'Received on', render: (_v, row) => <span style={row.allocated ? undefined : muted}>{formatReceiptDate(row.date)}</span> },
        { key: 'age_days', header: 'Age', align: 'right', render: (_v, row) => <span style={row.allocated ? undefined : muted}>{row.age_days} d</span> },
        { key: 'quantity', header: 'Received', align: 'right', render: (_v, row) => <span style={row.allocated ? undefined : muted}>{Math.round(row.quantity).toLocaleString()}</span> },
        {
            key: 'allocated',
            header: 'Still on hand',
            align: 'right',
            render: (_v, row) =>
                row.allocated ? <strong>{Math.round(row.allocated).toLocaleString()}</strong> : <span style={muted}>—</span>,
        },
        {
            key: 'bucket_index',
            header: 'Bucket',
            render: (_v, row) => {
                if (row.untraced) return <span style={muted}>untraced</span>;
                if (row.bucket_index == null) return <span style={muted}>assumed sold</span>;
                const isHighlighted = highlight === row.bucket_index;
                return <span style={{ fontWeight: isHighlighted ? 600 : undefined }}>{labels[row.bucket_index]} days</span>;
            },
        },
    ];

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
            <div className="w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200">
                <div className="flex items-start justify-between border-b border-slate-100 p-6 pb-4">
                    <div>
                        <h3 className="text-lg font-bold text-slate-900">Receipts behind this stock</h3>
                        <p className="mt-1 text-sm text-slate-500">
                            {skuLabel}
                            {warehouseName ? ` · at ${warehouseName}` : ''}
                            {data ? ` · ${Math.round(data.on_hand).toLocaleString()} on hand · as of ${formatReceiptDate(data.as_of_date)}` : ''}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="cursor-pointer rounded-full p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                    >
                        <IconX className="h-5 w-5" />
                    </button>
                </div>
                <div className="max-h-[60vh] overflow-y-auto p-6">
                    {loading ? (
                        <div className="py-8 text-center text-sm text-slate-400">Loading...</div>
                    ) : !data || receipts.length === 0 ? (
                        <div className="py-8 text-center text-sm text-slate-400">
                            No receipts in our history for this SKU
                        </div>
                    ) : (
                        <>
                            <DataSheet
                                data={receipts}
                                columns={columns}
                                getRowKey={(row, index) => `${row.grn_id ?? 'r'}-${row.warehouse_id}-${row.date}-${index}`}
                                bare
                                hideHeaderBar
                            />
                            <p className="mt-3 text-xs text-slate-500">
                                {byWarehouse
                                    ? 'What stands at this warehouse today, newest origin first: each line is a receipt, here or at the warehouse it was transferred from, still carrying its original date.'
                                    : 'Newest receipts first; on-hand is attributed to them until it runs out, the rest are assumed sold.'}
                                {data.older_than_history > 0 &&
                                    (byWarehouse
                                        ? ` ${Math.round(data.older_than_history).toLocaleString()} units could not be traced to any receipt.`
                                        : ` ${Math.round(data.older_than_history).toLocaleString()} units are older than the earliest receipt we hold.`)}
                                {highlight === 'older' && ' The cell you opened is that remainder.'}
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default AgeingReceiptsModal;
